import { Box, Checkbox, FormControlLabel, List, ListItem, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ScopeAccordionDrawerItem from '../../components/DealGPT/ScopeAccordionDrawerItem';
import { CollectionWithCreator, ScopePagePair, SummarizationFile } from '../../Types/dealGPT';
import { ChatMode } from '../../Types/enums';

interface SelectSummaryReferencesProps {
  currentMode: ChatMode;

  // Discovery mode selections
  summaryCollections?: CollectionWithCreator[];
  selectedCollections: CollectionWithCreator[];
  setSelectedCollections: React.Dispatch<React.SetStateAction<CollectionWithCreator[]>>;

  // Analysis mode selections
  selectedScopePages: ScopePagePair[];
  setSelectedScopePages: React.Dispatch<React.SetStateAction<ScopePagePair[]>>;

  // NEW: summary file data required to compute hierarchy and preselect
  summaryFiles?: SummarizationFile[];
  initialSummaryFileId?: string;
}

const SelectSummaryReferences: React.FC<SelectSummaryReferencesProps> = ({
  currentMode,

  summaryCollections,
  selectedCollections,
  setSelectedCollections,

  selectedScopePages,
  setSelectedScopePages,

  summaryFiles,
  initialSummaryFileId,
}) => {
  const [expandedScope, setExpandedScope] = useState<string | null>(null);

  const analysisHierarchy = useMemo(() => {
    if (!summaryFiles) return [];

    const map: Record<string, { scope: string; pages: string[] }> = {};

    summaryFiles.forEach((f) => {
      if (f.scope && f.pageName) {
        if (!map[f.scope]) map[f.scope] = { scope: f.scope, pages: [] };
        map[f.scope].pages.push(f.pageName);
      }
    });

    return Object.values(map);
  }, [summaryFiles]);

  useEffect(() => {
    if (!summaryFiles || !initialSummaryFileId) return;

    const sf = summaryFiles.find((f) => f.id === initialSummaryFileId);
    if (!sf) return;

    if (sf.collectionId && summaryCollections) {
      const col = summaryCollections.find((c) => c.id === sf.collectionId);
      if (col) {
        setSelectedCollections([col]);
      }
    } else if (sf.scope && sf.pageName) {
      setSelectedScopePages([{ scope: sf.scope, pageName: sf.pageName }]);
      setExpandedScope(sf.scope);
    }
  }, [
    summaryFiles,
    summaryCollections,
    initialSummaryFileId,
    setSelectedCollections,
    setSelectedScopePages,
  ]);

  const toggleCollection = useCallback(
    (col: CollectionWithCreator) => {
      setSelectedCollections((prev) =>
        prev.some((c) => c.id === col.id) ? prev.filter((c) => c.id !== col.id) : [...prev, col]
      );
    },
    [setSelectedCollections]
  );

  const toggleScopePage = useCallback(
    (pair: ScopePagePair) => {
      setSelectedScopePages((prev) =>
        prev.some((p) => p.scope === pair.scope && p.pageName === pair.pageName)
          ? prev.filter((p) => !(p.scope === pair.scope && p.pageName === pair.pageName))
          : [...prev, pair]
      );
    },
    [setSelectedScopePages]
  );

  // Toggle entire scope selection
  const toggleScope = useCallback(
    (scope: string, pages: string[]) => {
      setSelectedScopePages((prev) => {
        const selectedPages = prev.filter((p) => p.scope === scope).map((p) => p.pageName);
        const fullySelected = pages.every((p) => selectedPages.includes(p));

        if (fullySelected) {
          return prev.filter((p) => p.scope !== scope);
        }

        const pagesToAdd = pages
          .filter((p) => !selectedPages.includes(p))
          .map((pageName) => ({ scope, pageName }));

        return [...prev, ...pagesToAdd];
      });
    },
    [setSelectedScopePages]
  );

  return (
    <Box
      sx={{
        padding: 3,
        border: '1px solid #070154',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        margin: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        2. Select References
      </Typography>

      <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
        {currentMode === ChatMode.DISCOVERY ? (
          <List disablePadding>
            {summaryCollections?.map((col) => (
              <ListItem key={col.id} sx={{ pl: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCollections.some((c) => c.id === col.id)}
                      onChange={() => toggleCollection(col)}
                    />
                  }
                  label={col.name}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <List disablePadding>
            {analysisHierarchy.map(({ scope, pages }) => (
              <ScopeAccordionDrawerItem
                key={scope}
                scope={scope}
                pages={pages}
                selectedScopePages={selectedScopePages}
                toggleScope={toggleScope}
                toggleScopePage={toggleScopePage}
                expanded={expandedScope === scope}
                onToggleExpand={() => setExpandedScope(expandedScope === scope ? null : scope)}
              />
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default SelectSummaryReferences;
